import { useMemo } from 'react';

import { PaginationCursorRespone, PaginationOffsetResponse } from '@fly/shared';
import {
  PaginationState,
  TableOptions,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';

import { DataProcessing, PaginationType } from '../TanstackTable.types';

// 1. Define the Context/Input for any strategy
interface StrategyContext<T> {
  data: T[];
  paginationState: PaginationState;
  serverPagination?: PaginationOffsetResponse | PaginationCursorRespone;
}

// 2. Define the Output Shape (Tanstack Config Partial)
type TableStrategyResult<T> = Partial<TableOptions<T>> & {
  pageCount?: number;
  rowCount?: number;
};

// --- Concrete Strategies ---
const clientSideStrategy = <T>({
  paginationState,
}: StrategyContext<T>): TableStrategyResult<T> => ({
  manualPagination: false,
  getSortedRowModel: getSortedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  state: {
    pagination: {
      ...(paginationState as PaginationState),
      pageIndex: (paginationState?.pageIndex ?? 1) - 1,
    },
  },
});

const clientNoneStrategy = <T>({
  paginationState,
}: StrategyContext<T>): TableStrategyResult<T> => ({
  manualPagination: false,
  getSortedRowModel: getSortedRowModel(),
  getFilteredRowModel: getFilteredRowModel(),
});

const serverOffsetStrategy = <T>({
  serverPagination,
  paginationState,
}: StrategyContext<T>): TableStrategyResult<T> => {
  const p = serverPagination as PaginationOffsetResponse;
  return {
    pageCount: p ? Math.ceil(p.total / p.limit) : -1,
    rowCount: p?.total || -1,
    manualPagination: true,
    getSortedRowModel: undefined,
    getFilteredRowModel: undefined,
    getPaginationRowModel: undefined,
    state: {
      pagination: paginationState as PaginationState,
    },
  };
};

const serverCursorStrategy = <T>(): TableStrategyResult<T> => ({
  pageCount: undefined,
  rowCount: undefined,
  manualPagination: true,
  getSortedRowModel: undefined,
  getFilteredRowModel: undefined,
  getPaginationRowModel: undefined,
  state: {
    pagination: { pageIndex: 0, pageSize: 0 },
  },
});

/** Same as server_cursor: manual pagination, full data from parent; virtualizer only affects rendering. */
const serverVirtualScrollStrategy = <T>(): TableStrategyResult<T> => ({
  pageCount: undefined,
  rowCount: undefined,
  manualPagination: true,
  getSortedRowModel: undefined,
  getFilteredRowModel: undefined,
  getPaginationRowModel: undefined,
  state: {
    pagination: { pageIndex: 0, pageSize: 0 },
  },
});

// --- Registry ---

const strategies = {
  client_offset: clientSideStrategy,
  client_none: clientNoneStrategy,
  server_offset: serverOffsetStrategy,
  server_cursor: serverCursorStrategy,
  server_virtual_scroll: serverVirtualScrollStrategy,
};

// --- Hook ---

interface UseTableStrategyProps<T> {
  dataProcessing: DataProcessing;
  paginationType: PaginationType;
  data: T[];
  serverPagination?: PaginationOffsetResponse | PaginationCursorRespone;
  paginationState: PaginationState;
}

export function useTableStrategy<T>({
  dataProcessing,
  paginationType,
  data,
  serverPagination,
  paginationState,
}: UseTableStrategyProps<T>) {
  // Resolve key
  const strategyKey = `${dataProcessing}_${paginationType}`;

  return useMemo(() => {
    // We get the generic strategy function from the map
    const strategy =
      strategies[strategyKey as keyof typeof strategies] ||
      strategies.server_cursor;

    // Execute strategy with the correct generic type T
    const result = strategy({ data, paginationState, serverPagination });

    return {
      ...result,
      paginationState: result.state?.pagination,
    };
  }, [strategyKey, data, paginationState, serverPagination]);
}
